/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GraphicalTakeOffPoint } from '@gh/builder-shared-data';
import { createDialogRoute } from '@gh/common-shared';
import { DialogInput } from '@gh/core-ui';
import { newPoint } from '../../resources/models';

export interface GraphicalTakeOffZoomDialogConfig {
  image: HTMLImageElement;
  zoom: number;
  position: GraphicalTakeOffPoint;
}

const ZOOM_CANVAS_WIDTH = 300;
const ZOOM_CANVAS_HEIGHT = 300;

/**
 * Zoom component
 */
@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-zoom',
  templateUrl: 'graphical-take-off-zoom.component.html',
  styleUrls: ['graphical-take-off-zoom.component.scss'],
})
export class GraphicalTakeOffZoomComponent implements OnInit, DialogInput<GraphicalTakeOffZoomDialogConfig> {
  input: GraphicalTakeOffZoomDialogConfig;

  /**
   * Canvas
   *
   * @type {ElementRef}
   */
  @ViewChild('zoomCanvas', { static: true }) zoomCanvasEl: ElementRef;

  /**
   * Scale
   *
   * @type {number}
   */
  scale: number = 1.5;

  /**
   * Constructor
   *
   * @param data
   * @param {MatDialogRef<GraphicalTakeOffZoomComponent>} dialogRef
   */
  constructor(private dialogRef: MatDialogRef<GraphicalTakeOffZoomComponent>) {
  }

  @HostListener('document:contextmenu', ['$event'])
  menuContext(e: MouseEvent): boolean {
    e.preventDefault();
    return false;
  }

  /**
   * On init hook
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.drawImage();
  }

  /**
   * Draw zoomed image
   *
   * @returns {void}
   */
  drawImage(): void {
    const { image, position, zoom } = this.input;

    const canvasEl = <HTMLCanvasElement>this.zoomCanvasEl.nativeElement;
    const canvasCtx = canvasEl.getContext('2d')!;

    canvasEl.width = ZOOM_CANVAS_WIDTH;
    canvasEl.height = ZOOM_CANVAS_HEIGHT;

    const scaledWidth = ZOOM_CANVAS_WIDTH / zoom / this.scale;
    const scaledHeight = ZOOM_CANVAS_HEIGHT / zoom / this.scale;

    canvasCtx.drawImage(
      image,
      position.x - scaledWidth / 2,
      position.y - scaledHeight / 2,
      scaledWidth,
      scaledHeight,
      0,
      0,
      ZOOM_CANVAS_WIDTH,
      ZOOM_CANVAS_HEIGHT);
  }

  updateCursor(e: MouseEvent): void {
    this.zoomCanvasEl.nativeElement.style.cursor = this.getPoint(e) ? 'crosshair' : 'auto';
  }

  /**
   * Set view point
   *
   * @param {MouseEvent} e
   * @returns {void}
   */
  onClick(e: MouseEvent): void {
    const point = this.getPoint(e);

    if (point) {
      this.dialogRef.close(point);
    }
  }

  private getPoint(e: MouseEvent): Maybe<GraphicalTakeOffPoint> {
    const { position, zoom } = this.input;
    const scaledWidth = ZOOM_CANVAS_WIDTH / zoom / this.scale;
    const scaledHeight = ZOOM_CANVAS_HEIGHT / zoom / this.scale;

    const { offsetX, offsetY } = e;

    const x = position.x + offsetX / (ZOOM_CANVAS_WIDTH / scaledWidth) - scaledWidth / 2;
    const y = position.y + offsetY / (ZOOM_CANVAS_HEIGHT / scaledHeight) - scaledHeight / 2;

    if (x > 0 && y > 0) {
      return newPoint(x, y);
    }
  }
}

export const graphicalTakeOffZoomDialogRoute = createDialogRoute({
  component: GraphicalTakeOffZoomComponent,
  config: {
    width: '300px',
  },
});
